import React, { useEffect, useState } from "react";
import axios from "axios";

export default function Quiz() {
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [showResult, setShowResult] = useState(false);
  const [score, setScore] = useState(0);
  const [wrongQuestions, setWrongQuestions] = useState([]);

  const [subjects, setSubjects] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState("");
  const [questionCount, setQuestionCount] = useState(10);
  const [started, setStarted] = useState(false);

  // =========================
  // LOAD SUBJECT
  // =========================
  useEffect(() => {
    axios.get("/api/subjects/")
      .then(res => {
        const data = Array.isArray(res.data)
          ? res.data
          : res.data.results || [];
        setSubjects(data);
      });
  }, []);

  // =========================
  // LOAD QUESTIONS
  // =========================
  const fetchQuestions = () => {
    if (!selectedSubject) return alert("กรุณาเลือกวิชา");

    axios.get(`/api/questions/?subject=${selectedSubject}&page_size=300`)
      .then(res => {
        const data = Array.isArray(res.data)
          ? res.data
          : res.data.results || [];

        const shuffled = [...data].sort(() => 0.5 - Math.random());

        setQuestions(shuffled.slice(0, Math.min(questionCount, shuffled.length)));
        setStarted(true);
        setShowResult(false);
        setAnswers({});
        setScore(0);
        setWrongQuestions([]);
      });
  };

  // =========================
  // SELECT ANSWER
  // =========================
  const handleSelect = (qid, choice) => {
    setAnswers(prev => ({
      ...prev,
      [qid]: choice
    }));
  };

  // =========================
  // SUBMIT
  // =========================
  const handleSubmit = () => {
    let s = 0;
    let wrong = [];

    questions.forEach(q => {
      if (answers[q.id] === q.correct_answer) {
        s++;
      } else {
        wrong.push(q);
      }
    });

    setScore(s);
    setWrongQuestions(wrong);
    setShowResult(true);

    // 🔥 scroll ไปส่วนข้อผิด
    setTimeout(() => {
      document.getElementById("wrong-section")?.scrollIntoView({ behavior: "smooth" });
    }, 200);
  };

  // =========================
  // RESET
  // =========================
  const handleReset = () => {
    setAnswers({});
    setShowResult(false);
    setScore(0);
    setQuestions([]);
    setStarted(false);
    setWrongQuestions([]);
  };

  // =========================
  // PROGRESS
  // =========================
  const progress = questions.length > 0
    ? (Object.keys(answers).length / questions.length) * 100
    : 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 p-6">
      <div className="max-w-3xl mx-auto">

        <h1 className="text-3xl font-bold text-white text-center mb-6">
          📘 Logistics Quiz
        </h1>

        {/* ===== START ===== */}
        {!started && (
          <div className="bg-white rounded-2xl shadow-xl p-6 space-y-4">

            <h2 className="text-xl font-semibold text-gray-800">
              🧠 เริ่มทำข้อสอบ
            </h2>

            <select
              className="w-full p-2 border rounded-xl"
              value={selectedSubject}
              onChange={(e) => setSelectedSubject(e.target.value)}
            >
              <option value="">-- เลือกวิชา --</option>
              {subjects.map(s => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>

            <select
              className="w-full p-2 border rounded-xl"
              value={questionCount}
              onChange={(e) => setQuestionCount(Number(e.target.value))}
            >
              {[10,20,30,40,50,100,200,300,400,500].map(n => (
                <option key={n}>{n}</option>
              ))}
            </select>

            <button
              onClick={fetchQuestions}
              className="w-full bg-gradient-to-r from-indigo-500 to-pink-500 text-white py-3 rounded-xl"
            >
              🚀 เริ่มสอบ
            </button>
          </div>
        )}

        {/* ===== PROGRESS ===== */}
        {started && !showResult && (
          <div className="mb-4">
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div
                className="bg-gradient-to-r from-indigo-500 to-pink-500 h-3 rounded-full"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        )}

        {/* ===== QUESTIONS ===== */}
        {started && questions.map((q, index) => {
          const userAnswer = answers[q.id];
          const isCorrect = userAnswer === q.correct_answer;

          return (
            <div key={q.id} className="bg-white rounded-2xl shadow p-6 mb-4">

              <p className="font-semibold">
                {index + 1}. {q.question_text}
              </p>

              <div className="mt-3 space-y-2">
                {q.choices.map(choice => {
                  const isSelected = userAnswer === choice.key;

                  return (
                    <button
                      key={choice.key}
                      onClick={() => handleSelect(q.id, choice.key)}
                      disabled={showResult}
                      className={`
                        w-full text-left px-4 py-3 rounded-xl border
                        ${isSelected ? "bg-indigo-500 text-white" : ""}
                        ${showResult && choice.key === q.correct_answer && "bg-green-100"}
                        ${showResult && isSelected && choice.key !== q.correct_answer && "bg-red-100"}
                      `}
                    >
                      {choice.text}
                    </button>
                  );
                })}
              </div>

              {showResult && (
                <div className="mt-2 text-sm">
                  {isCorrect ? "✔ ถูก" : "❌ ผิด"}
                </div>
              )}
            </div>
          );
        })}

        {/* ===== SUBMIT ===== */}
        {started && !showResult && (
          <button
            onClick={handleSubmit}
            className="w-full mt-4 bg-gradient-to-r from-indigo-500 to-pink-500 text-white py-3 rounded-xl"
          >
            Submit
          </button>
        )}

        {/* ===== SCORE ===== */}
        {showResult && (
          <div className="bg-white rounded-2xl shadow-xl p-6 mt-4 text-center">
            <h2 className="text-2xl font-bold">
              🎯 Score: {score} / {questions.length}
            </h2>

            <button
              onClick={handleReset}
              className="mt-4 bg-green-500 text-white px-6 py-2 rounded-xl"
            >
              🔄 ทำใหม่
            </button>
          </div>
        )}

        {/* ===== WRONG QUESTIONS ===== */}
		{showResult && wrongQuestions.length > 0 && (
		  <div id="wrong-section" className="bg-white rounded-2xl shadow-xl p-6 mt-6">

			<h3 className="text-xl font-bold text-red-600 mb-4">
			  ❌ ข้อที่ตอบผิด ({wrongQuestions.length})
			</h3>

			<div className="space-y-4">
			  {wrongQuestions.map((q, index) => {

				return (
				  <div key={q.id} className="border p-4 rounded-xl bg-red-50">

					<p className="font-semibold">
					  {index + 1}. {q.question_text}
					</p>

					{/* 🔥 แสดงทุกตัวเลือก */}
					<div className="mt-3 space-y-2">
					  {q.choices.map(choice => {
						const isUser = choice.key === answers[q.id];
						const isCorrect = choice.key === q.correct_answer;

						return (
						  <div
							key={choice.key}
							className={`
							  px-3 py-2 rounded-lg border text-sm
							  ${isCorrect ? "bg-green-100 border-green-400" : ""}
							  ${isUser && !isCorrect ? "bg-red-100 border-red-400" : ""}
							`}
						  >
							{choice.text}

							{isCorrect && (
							  <span className="ml-2 text-green-600 font-medium">
								✔ คำตอบที่ถูก
							  </span>
							)}

							{isUser && !isCorrect && (
							  <span className="ml-2 text-red-600 font-medium">
								❌ คำตอบของคุณ
							  </span>
							)}
						  </div>
						);
					  })}
					</div>

				  </div>
				);
			  })}
			</div>

		  </div>
		)}

      </div>
    </div>
  );
}